/**
 * Invoice Form Page - Create/Edit Customer Invoice
 * Leviathan - Fuel Jobber Back Office
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface Customer {
  id: string;
  customerNumber: string;
  name: string;
  billingAddress: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingZip: string | null;
  paymentTerms: number | null;
  taxExempt: boolean;
}

interface Order {
  id: string;
  orderNumber: string;
  status: string;
  total: number;
  customerId: string;
  items: Array<{
    productId: string;
    product: { name: string; sku: string };
    quantity: number;
    unit: string;
    unitPrice: number;
    lineTotal: number;
  }>;
}

interface InvoiceItem {
  productId: string | null;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  isTaxable: boolean;
}

interface FormData {
  customerId: string;
  orderId: string;
  invoiceDate: string;
  paymentTerms: string;
  billingName: string;
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  poNumber: string;
  taxRate: number;
  discountAmount: number;
  shippingAmount: number;
  notes: string;
  internalNotes: string;
  items: InvoiceItem[];
}

const PAYMENT_TERMS = [
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'NET10', label: 'Net 10' },
  { value: 'NET15', label: 'Net 15' },
  { value: 'NET30', label: 'Net 30' },
  { value: 'NET45', label: 'Net 45' },
  { value: 'NET60', label: 'Net 60' },
  { value: 'NET90', label: 'Net 90' },
];

export default function InvoiceFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  const [formData, setFormData] = useState<FormData>({
    customerId: searchParams.get('customerId') || '',
    orderId: searchParams.get('orderId') || '',
    invoiceDate: new Date().toISOString().split('T')[0] || '',
    paymentTerms: 'NET30',
    billingName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    poNumber: '',
    taxRate: 0,
    discountAmount: 0,
    shippingAmount: 0,
    notes: '',
    internalNotes: '',
    items: [],
  });

  useEffect(() => {
    fetchCustomers();
    if (isEditMode) {
      fetchInvoice();
    }
  }, [id]);

  useEffect(() => {
    if (formData.customerId) {
      fetchOrdersForCustomer(formData.customerId);
      const customer = customers.find((c) => c.id === formData.customerId);
      if (customer) {
        // Auto-fill billing address from customer
        if (!isEditMode && !formData.billingAddress) {
          setFormData((prev) => ({
            ...prev,
            billingName: customer.name,
            billingAddress: customer.billingAddress || '',
            billingCity: customer.billingCity || '',
            billingState: customer.billingState || '',
            billingZip: customer.billingZip || '',
            paymentTerms: customer.paymentTerms ? `NET${customer.paymentTerms}` : 'NET30',
            taxRate: customer.taxExempt ? 0 : prev.taxRate,
          }));
        }
      }
    }
  }, [formData.customerId, customers]);

  useEffect(() => {
    // If order is selected, populate items from order
    if (formData.orderId && orders.length > 0) {
      const order = orders.find((o) => o.id === formData.orderId);
      if (order && formData.items.length === 0) {
        setFormData((prev) => ({
          ...prev,
          items: order.items.map((item) => ({
            productId: item.productId,
            description: `${item.product.name} (${item.product.sku})`,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
            isTaxable: true,
          })),
        }));
      }
    }
  }, [formData.orderId, orders]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers?status=ACTIVE&limit=500`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  const fetchOrdersForCustomer = async (customerId: string) => {
    try {
      const response = await fetch(
        `${API_URL}/api/orders?customerId=${customerId}&status=DELIVERED&limit=100`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  };

  const fetchInvoice = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const invoice = data.invoice;
        setFormData({
          customerId: invoice.customerId,
          orderId: invoice.orderId || '',
          invoiceDate: invoice.invoiceDate?.split('T')[0] || '',
          paymentTerms: invoice.paymentTerms || 'NET30',
          billingName: invoice.billingName || '',
          billingAddress: invoice.billingAddress || '',
          billingCity: invoice.billingCity || '',
          billingState: invoice.billingState || '',
          billingZip: invoice.billingZip || '',
          poNumber: invoice.poNumber || '',
          taxRate: invoice.taxRate || 0,
          discountAmount: invoice.discountAmount || 0,
          shippingAmount: invoice.shippingAmount || 0,
          notes: invoice.notes || '',
          internalNotes: invoice.internalNotes || '',
          items: invoice.items.map((item: InvoiceItem) => ({
            productId: item.productId,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
            isTaxable: item.isTaxable ?? true,
          })),
        });
      } else {
        setError('Invoice not found');
      }
    } catch (err) {
      setError('Failed to load invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: null,
          description: '',
          quantity: 1,
          unit: 'gallon',
          unitPrice: 0,
          lineTotal: 0,
          isTaxable: true,
        },
      ],
    }));
  };

  const updateItem = (index: number, field: string, value: string | number | boolean) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      if (!currentItem) return prev;

      const item = { ...currentItem, [field]: value };
      item.lineTotal = item.quantity * item.unitPrice;
      newItems[index] = item;
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.lineTotal, 0);
    const taxableAmount = formData.items
      .filter((item) => item.isTaxable)
      .reduce((sum, item) => sum + item.lineTotal, 0);
    const taxAmount = taxableAmount * (formData.taxRate / 100);
    const total = subtotal + taxAmount - formData.discountAmount + formData.shippingAmount;
    return { subtotal, taxAmount, total };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.customerId) {
      setError('Please select a customer');
      return;
    }

    if (formData.items.length === 0) {
      setError('Please add at least one line item');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/invoices/${id}` : `${API_URL}/api/invoices`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...formData,
          orderId: formData.orderId || null,
        }),
      });

      if (response.ok) {
        navigate('/invoices');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save invoice');
      }
    } catch (err) {
      setError('Failed to save invoice');
    } finally {
      setIsSaving(false);
    }
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading invoice...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Invoice' : 'New Invoice'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify invoice details' : 'Create a customer invoice'}
              </p>
            </div>
            <button
              onClick={() => navigate('/invoices')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider"
            >
              ← Back to Invoices
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer & Order Selection */}
        <Panel title="Invoice Details">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Customer *
              </label>
              <select
                value={formData.customerId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    customerId: e.target.value,
                    orderId: '',
                    items: [],
                  }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
                disabled={isEditMode}
              >
                <option value="">Select Customer...</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.customerNumber})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                From Order (Optional)
              </label>
              <select
                value={formData.orderId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, orderId: e.target.value, items: [] }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                disabled={!formData.customerId || isEditMode}
              >
                <option value="">No linked order</option>
                {orders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.orderNumber} - ${order.total.toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Invoice Date
              </label>
              <input
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Payment Terms
              </label>
              <select
                value={formData.paymentTerms}
                onChange={(e) => setFormData((prev) => ({ ...prev, paymentTerms: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {PAYMENT_TERMS.map((term) => (
                  <option key={term.value} value={term.value}>
                    {term.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Customer PO #
              </label>
              <input
                type="text"
                value={formData.poNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, poNumber: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Billing Address */}
        <Panel title="Billing Address">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Billing Name
              </label>
              <input
                type="text"
                value={formData.billingName}
                onChange={(e) => setFormData((prev) => ({ ...prev, billingName: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Street Address
              </label>
              <input
                type="text"
                value={formData.billingAddress}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, billingAddress: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                City
              </label>
              <input
                type="text"
                value={formData.billingCity}
                onChange={(e) => setFormData((prev) => ({ ...prev, billingCity: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={formData.billingState}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, billingState: e.target.value }))
                  }
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  ZIP
                </label>
                <input
                  type="text"
                  value={formData.billingZip}
                  onChange={(e) => setFormData((prev) => ({ ...prev, billingZip: e.target.value }))}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>
            </div>
          </div>
        </Panel>

        {/* Line Items */}
        <Panel title="Line Items">
          <div className="space-y-4">
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-text-tertiary font-mono">
                No items added yet.{' '}
                {formData.orderId ? 'Items will be loaded from order.' : 'Add items manually.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="border-b border-panel-border">
                    <tr>
                      <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Description
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Qty
                      </th>
                      <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Unit
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-28">
                        Price
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-28">
                        Total
                      </th>
                      <th className="text-center px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-16">
                        Tax
                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index} className="border-b border-panel-border">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm"
                            placeholder="Item description..."
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={item.quantity || ''}
                            onChange={(e) =>
                              updateItem(index, 'quantity', parseFloat(e.target.value) || 0)
                            }
                            min="0"
                            step="0.01"
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={item.unit}
                            onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={item.unitPrice || ''}
                            onChange={(e) =>
                              updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)
                            }
                            min="0"
                            step="0.001"
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-amber font-mono font-semibold">
                            ${item.lineTotal.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={item.isTaxable}
                            onChange={(e) => updateItem(index, 'isTaxable', e.target.checked)}
                            className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-error-red hover:text-error-red/80 font-mono text-sm"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              onClick={addItem}
              className="w-full border border-dashed border-panel-border hover:border-amber text-text-secondary hover:text-amber py-3 font-mono text-sm transition-colors"
            >
              + Add Line Item
            </button>
          </div>
        </Panel>

        {/* Totals & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Panel title="Notes">
            <div className="space-y-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Notes (visible on invoice)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Thank you for your business..."
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Internal Notes (not on invoice)
                </label>
                <textarea
                  value={formData.internalNotes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, internalNotes: e.target.value }))
                  }
                  rows={2}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>
            </div>
          </Panel>

          <Panel title="Invoice Totals">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary font-mono">Subtotal</span>
                <span className="text-text-primary font-mono">${subtotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Tax Rate (%)</span>
                  <input
                    type="number"
                    value={formData.taxRate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        taxRate: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-20 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">${taxAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Discount</span>
                  <input
                    type="number"
                    value={formData.discountAmount}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        discountAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-error-red font-mono">
                  -${formData.discountAmount.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Shipping</span>
                  <input
                    type="number"
                    value={formData.shippingAmount}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        shippingAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">
                  ${formData.shippingAmount.toFixed(2)}
                </span>
              </div>

              <div className="border-t border-panel-border pt-4 flex justify-between items-center">
                <span className="text-text-primary font-mono font-semibold text-lg">TOTAL</span>
                <span
                  className="text-amber font-mono font-bold text-2xl"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.5)' }}
                >
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
